"use client";

import { type CSSProperties, type RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useFreightDashboardSync } from "@/hooks/useFreightDashboardSync";
import { useIsPhone } from "@/hooks/useIsPhone";
import { HOME_VIEW_ID } from "@/components/desktop/wilsonViewContextRegistry";
import { ErrorState, FlowConnectors, FlowRow, FlowSiteCard, WilsonLockup, type FlowConnector } from "@/components/desktop/ui";
import { STALE_SHORT } from "../queryConfig.constants";
import { behindOf, buildFlowRows, connectorLane, foldSites, isLargeLayout, openOf, shouldFetchWarehouseFlow, type WarehouseFlowCounts, type WarehouseFlowView } from "./warehouseFlow.utils";
import { SAMPLE_WAREHOUSE_FLOW_VIEW } from "./warehouseFlowSample";
import { WarehouseFlowEyebrow, WarehouseFlowLegend } from "./WarehouseFlowHeader";
import { WarehouseFlowPhoneBoard, WarehouseFlowSkeleton } from "./WarehouseFlowPhoneBoard";
import { useWarehouseFlowNames } from "./useWarehouseFlowNames";
const ROW_GAP_PX = 14;
const BUCKET_LINE_GAP_PX = 16;
const UNMEASURED_COUNT = 1000;
const BOARD_STYLE: CSSProperties = {
  paddingLeft: "clamp(32px, 4.5vw, 64px)",
  paddingRight: "clamp(100px, 8vw, 128px)"
};
const LOCKUP_STYLE: CSSProperties = {
  right: 28,
  bottom: 24,
  opacity: 0.8
};
const SAMPLE_DIM_STYLE: CSSProperties = {
  opacity: 0.5
};
const CONTENT_STYLE: CSSProperties = {
  maxWidth: 1180
};
const HEADLINE_STYLE: CSSProperties = {
  fontSize: "clamp(30px, 3.1vw, 44px)",
  letterSpacing: "-0.028em"
};
const FLOW_GRID_STYLE: CSSProperties = {
  marginTop: "clamp(16px, 4.5vh, 44px)",
  gridTemplateColumns: "minmax(220px, 28%) minmax(56px, 1fr) minmax(280px, 40%)",
  gridTemplateRows: "minmax(0, 1fr)"
};
export function WarehouseFlowBackground({
  isCovered = false
}: {
  isCovered?: boolean;
}) {
  useFreightDashboardSync();
  const isPhone = useIsPhone();
  const t = useClientTranslations("desktop.warehouseFlow");
  const {
    data: org
  } = api.org.getMyOrg.useQuery();
  const {
    data: me,
    isPending: isUserPending
  } = api.user.getMyUser.useQuery();
  const isHidden = me?.isWarehouseFlowHidden === true;
  const {
    data: view,
    isError,
    refetch
  } = api.freightDashboard.warehouseFlow.useQuery(undefined, {
    staleTime: STALE_SHORT,
    enabled: shouldFetchWarehouseFlow({
      isCovered,
      isHidden
    })
  });
  if (isHidden) {
    return null;
  }
  if (!view && !isError && (isPhone || !isCovered)) {
    return <WarehouseFlowSkeleton />;
  }
  if (!view && isError) {
    return <div className="pointer-events-none absolute inset-0 bg-offwhite">
                <ErrorState className="pointer-events-auto" title={t("error.title")} description={t("error.description")} retryLabel={t("error.retry")} onRetry={() => void refetch()} />
            </div>;
  }
  const sampleNote = org?.logisticsEmail ? t("sample.noteWithEmail", {
    email: org.logisticsEmail
  }) : t("sample.note");
  const isSample = view?.loads === 0;
  const boardView = isSample ? SAMPLE_WAREHOUSE_FLOW_VIEW : view;
  const boardNote = isSample ? sampleNote : undefined;
  return <>
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-offwhite" />
            {boardView && !isUserPending && <div className="absolute inset-0 motion-safe:animate-fade-in" data-window-id={HOME_VIEW_ID}>
                    {isPhone ? <WarehouseFlowPhoneBoard view={boardView} sampleNote={boardNote} /> : <WarehouseFlowBoard view={boardView} isCovered={isCovered} sampleNote={boardNote} />}
                </div>}
        </>;
}
function useFitCount({
  boxRef,
  gapPx
}: {
  boxRef: RefObject<HTMLDivElement | null>;
  gapPx: number;
}): number {
  const [count, setCount] = useState(UNMEASURED_COUNT);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const fit = () => {
      const first = box.firstElementChild;
      if (!(first instanceof HTMLElement)) return;
      const next = Math.max(1, Math.floor((box.clientHeight + gapPx) / (first.offsetHeight + gapPx)));
      setCount(previous => previous === next ? previous : next);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [boxRef, gapPx]);
  return count;
}
function WarehouseFlowBoard({
  view: fullView,
  isCovered,
  sampleNote
}: {
  view: WarehouseFlowView;
  isCovered: boolean;
  sampleNote?: string;
}) {
  const isSample = sampleNote !== undefined;
  const dimStyle = isSample ? SAMPLE_DIM_STYLE : undefined;
  const t = useClientTranslations("desktop.warehouseFlow");
  const shape = fullView.shape;
  const isLarge = isLargeLayout(fullView);
  const siteGapPx = isLarge ? 40 : 20;
  const containerRef = useRef<HTMLDivElement>(null);
  const sitesBoxRef = useRef<HTMLDivElement>(null);
  const rowsBoxRef = useRef<HTMLDivElement>(null);
  const siteRefs = useRef(new Map<string, HTMLDivElement>());
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const [connectors, setConnectors] = useState<FlowConnector[]>([]);
  const maxSites = useFitCount({
    boxRef: sitesBoxRef,
    gapPx: siteGapPx
  });
  const maxRows = useFitCount({
    boxRef: rowsBoxRef,
    gapPx: ROW_GAP_PX
  });
  const view = useMemo(() => foldSites({
    view: fullView,
    maxSites
  }), [fullView, maxSites]);
  const rows = useMemo(() => buildFlowRows({
    view,
    maxRows
  }), [view, maxRows]);
  const summaryOf = (counts: WarehouseFlowCounts, {
    shouldShowDelivered
  }: {
    shouldShowDelivered: boolean;
  }) => {
    const parts = [t("summary.open", {
      count: openOf(counts)
    })];
    const behind = behindOf(counts);
    if (behind > 0) parts.push(t("summary.behind", {
      count: behind
    }));
    if (shouldShowDelivered && counts.stages.delivered > 0) {
      parts.push(t("summary.delivered", {
        count: counts.stages.delivered
      }));
    }
    return parts.join(" · ");
  };
  const {
    siteName,
    rowName
  } = useWarehouseFlowNames(shape);
  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const origin = container.getBoundingClientRect();
    const siteKinds = new Map(view.sites.map(site => [site.id, site.kind]));
    const next: FlowConnector[] = [];
    rows.forEach(row => {
      const rowElement = rowRefs.current.get(row.key);
      if (!rowElement) return;
      const rowBox = rowElement.getBoundingClientRect();
      row.bySite.forEach(({
        siteId,
        counts,
        mode
      }) => {
        const siteElement = siteRefs.current.get(siteId);
        if (!siteElement) return;
        const siteBox = siteElement.getBoundingClientRect();
        next.push({
          id: `${siteId}->${row.key}`,
          from: {
            x: siteBox.right + (siteKinds.get(siteId) === "site" ? 0 : BUCKET_LINE_GAP_PX) - origin.left,
            y: siteBox.top + siteBox.height / 2 - origin.top
          },
          to: {
            x: rowBox.left - 10 - origin.left,
            y: rowBox.top + 9 - origin.top
          },
          weight: 1,
          lane: connectorLane({
            counts,
            mode
          }),
          isMuted: row.kind === "others" || siteKinds.get(siteId) !== "site"
        });
      });
    });
    setConnectors(next);
  }, [view, rows]);
  useLayoutEffect(() => {
    measure();
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure]);
  const totalOpen = openOf(view.totals);
  const subParts = [t("delivered", {
    count: view.totals.stages.delivered
  })];
  if (view.isTruncated) subParts.push(t("truncated", {
    count: view.loads
  }));
  return <div ref={containerRef} className="pointer-events-none absolute inset-0 overflow-hidden bg-offwhite" data-sentry-component="WarehouseFlowBoard" data-sentry-source-file="WarehouseFlowBackground.tsx">
            <div className="absolute inset-0" style={dimStyle}>
                <FlowConnectors connectors={connectors} isPaused={isCovered} data-sentry-element="FlowConnectors" data-sentry-source-file="WarehouseFlowBackground.tsx" />
            </div>
            <WilsonLockup height={32} className="absolute" style={LOCKUP_STYLE} data-sentry-element="WilsonLockup" data-sentry-source-file="WarehouseFlowBackground.tsx" />
            <div className="relative flex h-full flex-col pb-[112px] pt-[84px]" style={BOARD_STYLE}>
                <div className="mx-auto flex h-full w-full flex-col" style={CONTENT_STYLE}>
                    <div className="flex flex-col" style={{
          gap: 12
        }}>
                        <WarehouseFlowEyebrow sampleNote={sampleNote} shouldShowMessage data-sentry-element="WarehouseFlowEyebrow" data-sentry-source-file="WarehouseFlowBackground.tsx" />
                        <h1 className="font-normal leading-[1.08] text-ink" style={{
            ...HEADLINE_STYLE,
            ...dimStyle
          }} aria-hidden={isSample || undefined}>
                            {t(`headline.${shape}`, {
              open: totalOpen,
              count: view.counterpartiesWithOpen
            })}{" "}
                            <span className="text-ink-3">
                                {t("behind", {
                count: behindOf(view.totals)
              })}
                            </span>
                        </h1>
                        <span className="text-ink-2" style={{
            fontSize: 14,
            ...dimStyle
          }} aria-hidden={isSample || undefined}>
                            {subParts.join(" · ")}
                        </span>
                        <div className="mt-1" style={dimStyle} aria-hidden={isSample || undefined}>
                            <WarehouseFlowLegend view={view} data-sentry-element="WarehouseFlowLegend" data-sentry-source-file="WarehouseFlowBackground.tsx" />
                        </div>
                    </div>

                    <div className="grid min-h-0 flex-1" style={{
          ...FLOW_GRID_STYLE,
          ...dimStyle
        }} aria-hidden={isSample || undefined}>
                        <div className="flex min-h-0 min-w-0 flex-col gap-3">
                            <span className="text-xs text-ink-3">
                                {t(`columns.${shape}.sites`)}
                            </span>
                            <div ref={sitesBoxRef} className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", isLarge ? "justify-center" : "justify-start")} style={{
              gap: siteGapPx
            }}>
                                {view.sites.map(site => <div key={site.id} style={site.kind === "site" ? undefined : {
                width: "fit-content"
              }} ref={element => {
                if (element) {
                  siteRefs.current.set(site.id, element);
                } else {
                  siteRefs.current.delete(site.id);
                }
              }}>
                                        <FlowSiteCard name={siteName(site)} summary={summaryOf(site.counts, {
                  shouldShowDelivered: site.kind === "site"
                })} stages={site.counts.stages} behind={site.counts.behind} variant={isLarge ? "large" : "compact"} isMuted={site.kind !== "site"} />
                                    </div>)}
                            </div>
                        </div>

                        <div aria-hidden="true" />

                        <div className="flex min-h-0 min-w-0 flex-col gap-3">
                            <span className="text-xs text-ink-3">
                                {t(`columns.${shape}.counterparties`)}
                            </span>
                            <div ref={rowsBoxRef} className="flex min-h-0 flex-1 flex-col overflow-hidden" style={{
              gap: ROW_GAP_PX
            }}>
                                {rows.map(row => <div key={row.key} className="min-w-0" ref={element => {
                if (element) {
                  rowRefs.current.set(row.key, element);
                } else {
                  rowRefs.current.delete(row.key);
                }
              }}>
                                        <FlowRow name={rowName(row)} place={row.kind === "party" && row.party.name ? row.party.place : null} summary={summaryOf(row.counts, {
                  shouldShowDelivered: row.kind === "party"
                })} stages={row.kind === "party" ? row.counts.stages : undefined} behind={row.kind === "party" ? row.counts.behind : undefined} isMuted={row.kind === "others"} />
                                    </div>)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>;
}